<script setup lang="ts">
import { defineAsyncComponent, ref, watch, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotificationStore } from '@/stores/notificationStore'
import type { INotification } from '@/types'
import { useScrollDirectionEvent } from '@/composables/useScrollDirectionEvent'
import { useFullscreenStore } from '@/stores/fullscreenStore'

const Notification = defineAsyncComponent(() => import('@/components/notification/Notification.vue'))

const notifs = useNotificationStore()
const fullscreenStore = useFullscreenStore()
const { freshNotifications } = storeToRefs(notifs)
const { isFullscreen } = storeToRefs(fullscreenStore)

const { scrollPosition, scrollDirection } = useScrollDirectionEvent()
const notifMenu = ref()

watchEffect(() => {
	if (scrollPosition.value) {
		notifMenu.value?.hide()
	}
})

const queue = ref<INotification[]>([])

function addToQueue(notif: INotification) {
	queue.value.push(notif)
	notif.new = false
	const current = notif

	setTimeout(() => {
		current.hide = true
		setTimeout(async () => {
			const index = queue.value.indexOf(current)
			if (index !== -1) {
				queue.value.splice(index, 1)
			}
		}, 300)
	}, typeof notif?.delay === 'number' ? notif.delay : 10000)
}

watch(() => freshNotifications.value, () => {
	freshNotifications.value.forEach((notif) => {
		if (notif.new && !notif.consumed) {
			addToQueue(notif)
		}
	})
}, { deep: true, immediate: true })

function closeNotification(id: number) {
	const index = queue.value.findIndex(notif => notif.id === id)
	if (index !== -1) {
		queue.value[index].hide = true
		queue.value.splice(index, 1)
	}
}
</script>

<template>
	<div
		id="notification-container"
		:class="(scrollDirection === 'up' && scrollPosition > 200) || isFullscreen ? 'move-up-panel ' : 'move-down-panel '"
	>
		<template
			v-for="notification in queue"
			:key="notification.id"
		>
			<Notification
				:red-dot="false"
				:data="notification"
				type="notifpanel"
				:class="{ hide: notification.hide }"
				class="notification bg-slate-700 py-2 px-4 rounded-md select-none cursor-pointer !w-[300px] xs:!w-[345px]"
				@close="closeNotification(notification.id)"
			/>
		</template>
	</div>
</template>

<style scoped>
@keyframes move-in {
  0% {
    transform: translate(-500px, -50px) rotate(12deg);
    opacity: 0;
  }
  100% {
    transform: translate(0, 0) rotate(0);
    opacity: 1;
  }
}

@keyframes move-out {
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    transform: translate(-500px, -50px) rotate(12deg);
    opacity: 0;
  }
}

#notification-container {
	@apply fixed left-2 z-[18] flex flex-col gap-2;
}

.notification {
  animation: move-in 0.2s linear 0s 1 normal forwards;
}

.notification.hide {
  animation: move-out 0.2s linear 0s 1 forwards;
}

@media (min-width: 768px) {
  .move-up-panel {
    top: 70px;
  }

  .move-down-panel {
    top: 70px;
  }
}

@media (max-width: 767px) {
  .move-up-panel {
    top: 70px;
  }

  .move-down-panel {
    top: 120px;
  }
}

.move-up-panel,
.move-down-panel {
  transition: top 0.3s ease-in-out;
}
</style>
